import { BASE_URL, COOKIE_TOKEN_KEY } from './Constants';
import { getTokenFromCookies, getUpdatedClosure } from './helpers';
import {
    CategoryImage,
    Closure,
    Language,
    Product,
    ProductCategory,
    StoreConfiguration,
    Table,
    User
} from './types';

const checkIfAuthorized = (res: Response) => {
    // unauthorized
    if (res.status === 401) {
        document.cookie = `${COOKIE_TOKEN_KEY}=`;
        throw Error('Something went wrong');
    }
};

const getWithToken = (url: string) =>
    fetch(`${BASE_URL}/${url}`, {
        headers: {
            Authorization: `Bearer ${getTokenFromCookies()}`
        }
    }).then(res => {
        checkIfAuthorized(res);

        if (res.status === 200) {
            return res.json();
        }

        return res.text();
    });

const postWithToken = (url: string, data: Record<string, unknown>) =>
    fetch(`${BASE_URL}/${url}`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
            Authorization: `Bearer ${getTokenFromCookies()}`,
            'Content-Type': 'application/json'
        }
    }).then(res => {
        checkIfAuthorized(res);

        if (res.status === 403) {
            throw new Error('You are not allowed to perform this action');
        }

        return res;
    });

export const login = (username: string, password: string, storeID: number) =>
    fetch(`${BASE_URL}/Account/Login`, {
        method: 'POST',
        body: JSON.stringify({
            username,
            password,
            storeID
        }),
        headers: { 'Content-Type': 'application/json' }
    })
        .then(res => {
            checkIfAuthorized(res);

            if (res.status === 200) {
                return res.json();
            }

            throw Error('Please check your credentials and try again');
        })
        .then(({ token }: { token: string }) => {
            document.cookie = `${COOKIE_TOKEN_KEY}=${token}`;
        });

export const getUserAccount = (): Promise<User> =>
    getWithToken('Account/GetUserDetails');

export const getProductCategories = (): Promise<ProductCategory[]> =>
    getWithToken('ProductCategories');

export const getProducts = (): Promise<Product[]> => getWithToken('Products');

export const saveUpdateProductCategory = (
    category: Partial<ProductCategory>
): Promise<ProductCategory> =>
    postWithToken('ProductCategories/AddUpdate', category).then(res => res.json());

export const saveUpdateProduct = (product: Partial<Product>): Promise<Product> =>
    postWithToken('Products/AddUpdate', product).then(res => res.json());

export const deleteProduct = (id: Product['id']): Promise<any> =>
    postWithToken('Products/DeleteSingle', { entityID: id }).then(res => res.text());

export const getCategoryImages = (): Promise<CategoryImage[]> =>
    getWithToken('PredefinedImages');

export const getTables = (): Promise<Table[]> => getWithToken('Tables/GetAll');

export const saveUpdateTable = (table: Partial<Table>): Promise<Table> =>
    postWithToken('Tables/AddUpdate', table).then(res => res.json());

export const getStoreConfiguration = (): Promise<StoreConfiguration> =>
    getWithToken('StoreConfiguration');

export const getAvailableLanguages = (): Promise<Language[]> =>
    getWithToken('Misc/GetAvailableLanguages');

export const updateStoreConfiguration = (
    data: Partial<StoreConfiguration>
): Promise<StoreConfiguration> =>
    postWithToken('StoreConfiguration/Update', data).then(res => res.json());

export const generatePDF = data => postWithToken('PdfGeneration/DoGenerate', data);

export const resetPassword = data => postWithToken('Account/ResetOwnPassword', data);

export const getUsers = (): Promise<User[]> => getWithToken('Account/GetUsersInStore');

export const saveUpdateUser = (user: Partial<User>) =>
    postWithToken('Account/AddUpdate', user).then(res => res.json());

export const saveUpdateClosure = (values): Promise<Closure> =>
    postWithToken('WorkingHours/AddOverride', values)
        .then(res => res.json())
        .then(cl => getUpdatedClosure(cl));

export const deleteClosure = values =>
    postWithToken('WorkingHours/DeleteOverride', values);

export const getClosures = (): Promise<Closure[]> =>
    getWithToken('WorkingHours/GetClosures').then((closures: Closure<string>[]) =>
        closures.map(getUpdatedClosure)
    );
